import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Environment, Workspace } from "@spoonity/flags-contract";
import { listEnvironments, listWorkspaces } from "../api/endpoints";
import { useToast } from "../components/Toast";

const WS_KEY = "spoonity-flags.admin.workspace";
const ENV_KEY = "spoonity-flags.admin.environment";

function readStored(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string | null): void {
  try {
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

interface WorkspaceEnvState {
  loading: boolean;
  workspaces: Workspace[];
  environments: Environment[];
  workspace: Workspace | null;
  environment: Environment | null;
  selectWorkspace: (id: string) => void;
  selectEnvironment: (id: string) => void;
  reload: () => Promise<void>;
}

const Ctx = createContext<WorkspaceEnvState | null>(null);

export function WorkspaceEnvProvider({ children }: { children: ReactNode }) {
  const { pushError } = useToast();
  const [loading, setLoading] = useState(true);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [workspaceId, setWorkspaceId] = useState<string | null>(readStored(WS_KEY));
  const [environmentId, setEnvironmentId] = useState<string | null>(readStored(ENV_KEY));

  const loadWorkspaces = useCallback(async () => {
    setLoading(true);
    try {
      const page = await listWorkspaces({ limit: 200 });
      setWorkspaces(page.items);
      setWorkspaceId((cur) => {
        if (cur && page.items.some((w) => w.id === cur)) return cur;
        return page.items[0]?.id ?? null;
      });
    } catch (err) {
      pushError(err, "Could not load workspaces");
    } finally {
      setLoading(false);
    }
  }, [pushError]);

  useEffect(() => {
    void loadWorkspaces();
  }, [loadWorkspaces]);

  useEffect(() => {
    writeStored(WS_KEY, workspaceId);
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) {
      setEnvironments([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const page = await listEnvironments(workspaceId, { limit: 200 });
        if (cancelled) return;
        setEnvironments(page.items);
        setEnvironmentId((cur) => {
          if (cur && page.items.some((e) => e.id === cur)) return cur;
          return page.items[0]?.id ?? null;
        });
      } catch (err) {
        if (!cancelled) pushError(err, "Could not load environments");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, pushError]);

  useEffect(() => {
    writeStored(ENV_KEY, environmentId);
  }, [environmentId]);

  const value = useMemo<WorkspaceEnvState>(
    () => ({
      loading,
      workspaces,
      environments,
      workspace: workspaces.find((w) => w.id === workspaceId) ?? null,
      environment: environments.find((e) => e.id === environmentId) ?? null,
      selectWorkspace: (id: string) => setWorkspaceId(id),
      selectEnvironment: (id: string) => setEnvironmentId(id),
      reload: loadWorkspaces,
    }),
    [loading, workspaces, environments, workspaceId, environmentId, loadWorkspaces],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useWorkspaceEnv(): WorkspaceEnvState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useWorkspaceEnv must be used within WorkspaceEnvProvider");
  return ctx;
}
